import './index.scss'

import { useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import Modal, { ModalButtonStyle } from '@components/Modal'
import SeriesController from '@data/controller/SeriesController'
import { ChurchSeries } from '@interfaces/series.interface'

import closeIcon from 'url:@icon/close.svg';
import warningIcon from 'url:@icon/error-icon.svg';

const HTTP_NOT_FOUND = 404;

// Styled after the service and Launchpad delete modals.
export default function DeleteSeriesModal(props: {
  isOpen: boolean,
  series: ChurchSeries,
  close: () => void,
  onDeleted: () => void
}) {
  const auth = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = props.series.attributes.name || 'this series';

  const deleteSeries = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await SeriesController.deleteSeries(auth.churchId, props.series.id, auth);

      // Already gone, most likely deleted elsewhere: the outcome asked for.
      if (response.error && response.status !== HTTP_NOT_FOUND) {
        setError(response.error);
        return;
      }

      props.onDeleted();
    } finally {
      setIsDeleting(false);
    }
  };

  return <Modal
    id='delete-series-modal'
    isOpen={props.isOpen}
    footerButtons={[
      {
        title: isDeleting ? 'Deleting...' : 'Delete Series',
        style: ModalButtonStyle.FILLED,
        enabled: !isDeleting,
        onClick: () => {
          void deleteSeries();
        }
      },
      {
        title: 'Cancel',
        style: ModalButtonStyle.OUTLINE,
        enabled: !isDeleting,
        onClick: props.close
      }
    ]}>
    <div className='delete-series-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='close'
          disabled={isDeleting}
          onClick={props.close}>
          <img className='icon' src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>Delete Series</h3>
      </div>

      {error ? <p className='save-error'>{error}</p> : null}

      <div className='body'>
        <img src={warningIcon} alt='' aria-hidden='true' className='warning-icon' />

        <p className='warning-copy'>
          <strong>Are you sure you want to delete {name}?</strong>
        </p>

        <p className='warning-copy'>
          It will be removed from Launchpad and the Salt app. Its services aren't deleted: they stay in All
          Services, just no longer part of a series. This action cannot be undone.
        </p>
      </div>
    </div>
  </Modal>
}
